import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
  Modal,
  Table,
  Badge,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUser,
  FaCreditCard,
  FaUniversity,
  FaRupeeSign,
  FaExchangeAlt,
  FaReceipt,
  FaMobile,
  FaCalculator,
} from "react-icons/fa";
import Swal from "sweetalert2";

import PPIDmtService from "../../../services/PPIDmtService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SideBar from "../../../components/Sidebar";

const FundTransfer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { customerMobile, data, beneficiary } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [commercialData, setCommercialData] = useState(null);
  const [showChargesModal, setShowChargesModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [transferToken, setTransferToken] = useState("");
  const [transferResult, setTransferResult] = useState(null);
  const [errors, setErrors] = useState({});

  // Validate amount
  const validateAmount = () => {
    const newErrors = {};

    if (!amount.trim()) {
      newErrors.amount = "Amount is required";
    } else if (isNaN(amount) || parseFloat(amount) <= 0) {
      newErrors.amount = "Enter a valid amount";
    } else if (parseFloat(amount) < 100) {
      newErrors.amount = "Minimum amount is ₹100";
    } else if (parseFloat(amount) > 50000) {
      newErrors.amount = "Maximum amount is ₹50,000";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch commercial data
  const fetchCommercial = async () => {
    if (!validateAmount()) {
      return;
    }

    setLoading(true);
    try {
      const result = await PPIDmtService.fetchCommercial(amount);

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: result.message || "Failed to fetch charges",
        });
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setCommercialData(res.data);
        setShowChargesModal(true); // Show charges in modal
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: res.message || "Something went wrong",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Initiate fund transfer
  const initiateTransfer = async () => {
    if (!commercialData) {
      Swal.fire({
        icon: "warning",
        title: "Please calculate charges first",
        timer: 2000,
      });
      return;
    }

    setLoading(true);
    try {
      const result = await PPIDmtService.fundTransferGenerateOtp(
        data?.token,
        customerMobile,
        beneficiary.beneficiary_name,
        beneficiary.beneficiary_id,
        beneficiary.account_number,
        beneficiary.ifsc_code,
        amount
      );

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: result.message || "Transfer initiation failed",
        });
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setTransferToken(res.data.token);
        setShowChargesModal(false); // Close charges modal
        setShowOtpModal(true); // Open OTP modal
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: res.message || "Something went wrong",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and complete transfer
  const verifyOtpAndTransfer = async () => {
    if (!otp.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Please enter OTP",
        timer: 2000,
      });
      return;
    }

    setLoading(true);
    try {
      const result = await PPIDmtService.fundTransferVerifyOtp(
        data?.token,
        customerMobile,
        beneficiary.beneficiary_name,
        beneficiary.beneficiary_id,
        beneficiary.account_number,
        beneficiary.ifsc_code,
        amount,
        transferToken,
        otp
      );

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: result.message || "OTP verification failed",
        });
        return;
      }

      const res = result.data;

      // Check if status is ERROR - show error and don't show receipt
      if (res.status === "ERROR") {
        Swal.fire({
          icon: "error",
          title: "Transaction Failed",
          text: res.message || "Transaction could not be completed",
        });
        setShowOtpModal(false);
        return;
      }

      // For other statuses (SUCCESS, PENDING, FAILED), show receipt
      // setTransferResult({
      //   ...res.data,
      //   message: res.message,
      //   status: res.status,
      // });
      navigate("/ppi-money-transfer/receipt", {
        state: { customerMobile, wallet_data: data, responseData: res },
      });

      setShowOtpModal(false);
      setShowReceiptModal(true);
      setOtp("");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error.response?.data?.message || error.message || "Network error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/ppi-money-transfer/sender-home", {
      state: { customerMobile, data },
    });
  };

  // Format currency
  const formatCurrency = (value) => {
    return `₹${parseFloat(value || 0).toFixed(2)}`;
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "SUCCESS":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          {/* Beneficiary Info Card */}
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-white">
                <FaUser className="me-2" /> Beneficiary Details
              </h5>
              <Badge bg="light" text="dark">
                IMPS
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted d-block">
                      Beneficiary Name
                    </small>
                    <strong>{beneficiary?.beneficiary_name || "N/A"}</strong>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Account Number</small>
                    <strong>{beneficiary?.account_number || "N/A"}</strong>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted d-block">Bank Name</small>
                    <strong>{beneficiary?.bank_name || "N/A"}</strong>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">IFSC Code</small>
                    <strong>{beneficiary?.ifsc_code || "N/A"}</strong>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  {beneficiary?.is_ac_validate === 1 ? (
                    <Badge bg="success" className="px-3 py-2">
                      <FaCheckCircle className="me-2" /> Verified Account
                    </Badge>
                  ) : (
                    <Badge bg="warning" className="px-3 py-2">
                      <FaExclamationTriangle className="me-2" /> Unverified
                      Account
                    </Badge>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Transfer Form Card */}
          <Card className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">
                <FaExchangeAlt className="me-2" /> Transfer Details
              </h5>
            </Card.Header>
            <Card.Body>
              <Form>
                {/* Amount Input */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    <FaRupeeSign className="me-2" /> Transfer Amount
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount (100 - 50,000)"
                      min="100"
                      max="50000"
                      step="1"
                      isInvalid={!!errors.amount}
                      disabled={loading}
                    />
                  </InputGroup>
                  <Form.Control.Feedback type="invalid">
                    {errors.amount}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Minimum: ₹100 | Maximum: ₹50,000
                  </Form.Text>
                </Form.Group>

                {/* Transfer Mode - Fixed as IMPS */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Transfer Mode</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaExchangeAlt />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      value="IMPS"
                      readOnly
                      className="bg-light"
                    />
                  </InputGroup>
                  <Form.Text className="text-success">
                    <FaCheckCircle className="me-1" /> IMPS provides instant
                    transfer
                  </Form.Text>
                </Form.Group>

                {/* Buttons */}
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={handleGoBack}
                    disabled={loading}
                    className="flex-grow-1"
                  >
                    <FaArrowLeft className="me-2" /> Back to Dashboard
                  </Button>
                  <Button
                    variant="primary"
                    onClick={fetchCommercial}
                    disabled={loading || !amount}
                    className="flex-grow-1"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Calculating...
                      </>
                    ) : (
                      <>
                        <FaCalculator className="me-2" />
                        Calculate Charges
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charges Modal */}
      <Modal
        show={showChargesModal}
        onHide={() => setShowChargesModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-success">
          <Modal.Title className="text-success">
            <FaCalculator className="me-2" /> Transaction Charges
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {commercialData && (
            <>
              <Table bordered hover className="mb-0">
                <tbody>
                  <tr>
                    <td>
                      <strong>Transfer Amount</strong>
                    </td>
                    <td className="text-end">{formatCurrency(amount)}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Customer Charges</strong>
                    </td>
                    <td className="text-end">
                      {formatCurrency(commercialData.customer_charges)}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Customer Payable</strong>
                    </td>
                    <td className="text-end">
                      {formatCurrency(commercialData.customer_payable)}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Commission</strong>
                    </td>
                    <td className="text-end">
                      {formatCurrency(commercialData.commission)}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Costing</strong>
                    </td>
                    <td className="text-end">
                      {formatCurrency(commercialData.costing)}
                    </td>
                  </tr>
                  <tr className="table-success">
                    <td>
                      <strong>Payable</strong>
                    </td>
                    <td className="text-end fw-bold">
                      {formatCurrency(commercialData.payable)}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <Alert variant="info" className="mt-3">
                <p className="mb-0 small">
                  <strong>Note:</strong> These charges will be deducted from
                  your wallet.
                </p>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowChargesModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={initiateTransfer}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Processing...
              </>
            ) : (
              <>
                <FaExchangeAlt className="me-2" />
                Proceed to Transfer
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* OTP Verification Modal */}
      <Modal
        show={showOtpModal}
        onHide={() => setShowOtpModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="border-primary">
          <Modal.Title className="text-primary">
            <FaMobile className="me-2" /> Enter OTP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <p className="mb-0">
              OTP has been sent to your registered mobile number ending with ***
              {customerMobile?.slice(-3)}
            </p>
          </Alert>
          <Form.Group>
            <Form.Label>Enter 6-digit OTP</Form.Label>
            <Form.Control
              type="text"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="Enter OTP"
              maxLength={6}
              className="text-center fs-4"
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOtpModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={verifyOtpAndTransfer}
            disabled={loading || otp.length !== 6}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Verifying...
              </>
            ) : (
              "Verify & Transfer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Receipt Modal */}
      <Modal
        show={showReceiptModal}
        onHide={() => {
          setShowReceiptModal(false);
          handleGoBack();
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-success">
          <Modal.Title className="text-success">
            <FaReceipt className="me-2" /> Transaction Receipt
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {transferResult && (
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0 text-center">
                  Transaction{" "}
                  {transferResult.status === "SUCCESS"
                    ? "Successful"
                    : transferResult.status === "PENDING"
                    ? "Pending"
                    : "Failed"}
                </h5>
              </Card.Header>
              <Card.Body>
                {/* Status Icon */}
                <div className="text-center mb-4">
                  {transferResult.status === "SUCCESS" ? (
                    <div className="bg-success rounded-circle p-3 d-inline-block">
                      <FaCheckCircle size={48} className="text-white" />
                    </div>
                  ) : transferResult.status === "PENDING" ? (
                    <div className="bg-warning rounded-circle p-3 d-inline-block">
                      <FaExclamationTriangle size={48} className="text-white" />
                    </div>
                  ) : (
                    <div className="bg-danger rounded-circle p-3 d-inline-block">
                      <FaExclamationTriangle size={48} className="text-white" />
                    </div>
                  )}
                  <h4
                    className={`mt-3 ${
                      transferResult.status === "SUCCESS"
                        ? "text-success"
                        : transferResult.status === "PENDING"
                        ? "text-warning"
                        : "text-danger"
                    }`}
                  >
                    {transferResult.status === "SUCCESS"
                      ? "Transfer Completed"
                      : transferResult.status === "PENDING"
                      ? "Transfer Pending"
                      : "Transfer Failed"}
                  </h4>
                  {transferResult.message && (
                    <Alert
                      variant={
                        transferResult.status === "SUCCESS"
                          ? "success"
                          : transferResult.status === "PENDING"
                          ? "warning"
                          : "danger"
                      }
                      className="mt-2"
                    >
                      {transferResult.message}
                    </Alert>
                  )}
                </div>

                <Table borderless className="mb-0">
                  <tbody>
                    <tr>
                      <td>
                        <strong>Order ID:</strong>
                      </td>
                      <td className="text-end">
                        {transferResult.order_id || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Status:</strong>
                      </td>
                      <td className="text-end">
                        <Badge bg={getStatusBadgeColor(transferResult.status)}>
                          {transferResult.status}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Date & Time:</strong>
                      </td>
                      <td className="text-end">
                        {transferResult.timestamp ||
                          new Date().toLocaleString()}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Amount:</strong>
                      </td>
                      <td className="text-end">{formatCurrency(amount)}</td>
                    </tr>
                    {commercialData && (
                      <>
                        <tr>
                          <td>
                            <strong>Charges:</strong>
                          </td>
                          <td className="text-end">
                            {formatCurrency(commercialData.customer_charges)}
                          </td>
                        </tr>
                        <tr className="border-top">
                          <td>
                            <strong>Total:</strong>
                          </td>
                          <td className="text-end fw-bold">
                            {formatCurrency(commercialData.customer_payable)}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td>
                        <strong>Beneficiary:</strong>
                      </td>
                      <td className="text-end">
                        {beneficiary?.beneficiary_name}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Account:</strong>
                      </td>
                      <td className="text-end">
                        {beneficiary?.account_number}
                      </td>
                    </tr>
                    {transferResult.rrn && (
                      <tr>
                        <td>
                          <strong>UTR/Reference:</strong>
                        </td>
                        <td className="text-end">{transferResult.rrn}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <Alert variant="light" className="mt-4">
                  <p className="mb-0 small">
                    <strong>Note:</strong>{" "}
                    {transferResult.status === "SUCCESS"
                      ? "Please save this receipt for future reference. The amount will be credited to beneficiary account within few minutes."
                      : transferResult.status === "PENDING"
                      ? "Your transaction is pending. Please check status after some time or contact support."
                      : "Transaction failed. Please try again or contact support for assistance."}
                  </p>
                </Alert>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => {
              window.print();
            }}
          >
            Print Receipt
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowReceiptModal(false);
              handleGoBack();
            }}
          >
            Back to Dashboard
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FundTransfer;
